import { msg } from '@lingui/core/macro';

/**
 * Date utility functions for formatting and comparing dates
 */

/**
 * Checks if a date is today
 */
export const isToday = (date: Date): boolean => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

/**
 * Checks if a date is tomorrow
 */
export const isTomorrow = (date: Date): boolean => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return (
    date.getDate() === tomorrow.getDate() &&
    date.getMonth() === tomorrow.getMonth() &&
    date.getFullYear() === tomorrow.getFullYear()
  );
};

/**
 * Checks if two dates are the same day
 */
export const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
};

/**
 * Formats a date to display format
 * Returns "Today" for today, "Tomorrow" for tomorrow, or localized date format for other dates
 *
 * @param date - The date to format
 * @param translate - Lingui translation function from useLingui()._
 * @param locale - Optional locale string (defaults to navigator.language or 'de-DE')
 * @returns Formatted date string
 */
export const formatDateDisplay = (
  date: Date,
  translate: (message: ReturnType<typeof msg>) => string,
  locale?: string
): string => {
  const effectiveLocale = locale || navigator.language || 'de-DE';

  if (isToday(date)) {
    return translate(msg`Today`);
  }

  if (isTomorrow(date)) {
    return translate(msg`Tomorrow`);
  }

  // Use localized date format (e.g., "Wed, Jan 13" in English)
  return date.toLocaleDateString(effectiveLocale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Formats time to display format using Intl localization
 * Will use 12-hour or 24-hour format based on user's locale
 *
 * @param date - The date to format
 * @param locale - Optional locale string (defaults to navigator.language or 'de-DE')
 * @returns Formatted time string
 */
export const formatTimeDisplay = (date: Date, locale?: string): string => {
  const effectiveLocale = locale || navigator.language || 'de-DE';
  return date.toLocaleTimeString(effectiveLocale, {
    hour: 'numeric',
    minute: '2-digit',
  });
};
